import {defineStore} from 'pinia'
import {computed, ref} from 'vue'
import {useRouter} from 'vue-router'
import {ElMessage} from 'element-plus'
import Cookies from 'js-cookie'
import {globalState} from "@/utils/globalState"

const API_URL = process.env.VUE_APP_API_URL || 'https://www.echosphere.ltd'

export const useAuthStore = defineStore('auth', () => {
    const router = useRouter()

    // 状态
    const user = ref(null)
    const accessToken = ref(Cookies.get('access_token') || null)
    const refreshToken = ref(Cookies.get('refresh_token') || null)
    const permissions = ref([])
    const isLoading = ref(false)

    // 计算属性
    const isAuthenticated = computed(() => !!accessToken.value)
    const isAdmin = computed(() => user.value?.role === 'admin')
    const isManager = computed(() => user.value?.role === 'manager')
    const userInitials = computed(() => {
        if (!user.value) return ''
        const name = user.value.full_name || user.value.username
        return name.charAt(0).toUpperCase()
    })

    // 方法
    const setTokens = (access, refresh) => {
        accessToken.value = access
        refreshToken.value = refresh

        if (access) {
            Cookies.set('access_token', access, {expires: 7, path: '/'})
        } else {
            Cookies.remove('access_token', {path: '/'})
        }

        if (refresh) {
            Cookies.set('refresh_token', refresh, {expires: 30, path: '/'})
        } else {
            Cookies.remove('refresh_token', {path: '/'})
        }
    }

    const setUser = (userData) => {
        console.log('setUser 调用:', userData)

        if (!userData) {
            console.log('setUser: userData 为空，清空用户')
            user.value = null
            permissions.value = []
            localStorage.removeItem('user_info')
            globalState.clear()
            return
        }

        // 确保 userData 是对象
        if (typeof userData !== 'object' || userData === null) {
            console.error('setUser: userData 不是有效对象:', userData)
            return
        }

        // 检查是否有必要的字段
        if (!userData.id || !userData.username) {
            console.warn('setUser: 用户数据缺少必要字段，可能不完整', userData)
            // 不直接返回，因为可能是在更新部分信息
        }

        // 深度合并用户信息
        user.value = {
            ...user.value, // 保留现有属性
            ...userData    // 用新数据覆盖
        }

        // 确保重要字段存在
        if (userData.permissions) {
            permissions.value = userData.permissions
        }
        console.log('合并后的用户信息:', user.value)

        if (userData.permissions) {
            permissions.value = userData.permissions
        }

        // 同步到全局状态
        if (user.value) {
            globalState.setUser(user.value)
        }

        // 存储用户信息到localStorage
        try {
            localStorage.setItem('user_info', JSON.stringify(user.value))
            console.log('setUser: 保存到localStorage成功，用户ID:', user.value?.id)
        } catch (e) {
            console.error('setUser: 保存到localStorage失败:', e)
        }

        // 标记为已初始化
        if (user.value.id && user.value.username) {
            globalState.setInitialized()
            console.log('setUser: 标记为已初始化')
        }
    }

    // 合并用户信息的方法
    const mergeUserInfo = async (loginUserInfo) => {
        try {
            console.log('开始合并用户信息...')

            // 先设置登录接口返回的用户信息
            setUser(loginUserInfo)

            // 然后获取详细的个人信息
            const profileResponse = await fetch(`${API_URL}/work/auth/profile`, {
                headers: {
                    'Authorization': `Bearer ${accessToken.value}`
                }
            })

            if (!profileResponse.ok) {
                throw new Error('获取用户详细信息失败')
            }

            const profileData = await profileResponse.json()
            console.log('获取到个人信息:', profileData)

            // 合并用户信息
            let mergedUser = {
                ...loginUserInfo,
                ...(profileData.data || profileData.user || {}),
                // 确保重要字段不会被覆盖
                is_admin: loginUserInfo.is_admin || profileData.data?.is_admin || false
            }

            console.log('合并后的用户信息:', mergedUser)
            setUser(mergedUser)

            return mergedUser
        } catch (error) {
            console.error('合并用户信息失败:', error)
            // 如果获取个人信息失败，至少保存登录信息
            setUser(loginUserInfo)
            return loginUserInfo
        }
    }

    const clearAuth = () => {
        user.value = null
        accessToken.value = null
        refreshToken.value = null
        permissions.value = []

        Cookies.remove('access_token', {path: '/'})
        Cookies.remove('refresh_token', {path: '/'})
        localStorage.removeItem('user_info')
        globalState.clear()
    }

    const login = async (credentials) => {
        try {
            isLoading.value = true

            // 调用后端API进行登录
            const response = await fetch(`${API_URL}/work/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(credentials)
            })

            if (!response.ok) {
                throw new Error('登录失败')
            }

            const data = await response.json()

            setTokens(data.access_token, data.refresh_token)
            // 合并用户信息
            if (data.user) {
                await mergeUserInfo(data.user)
            }

            ElMessage.success(data.msg || '登录成功')

            // 派发登录成功事件
            window.dispatchEvent(new CustomEvent('user-login-success', {
                detail: {user: user.value}
            }))

            await router.push('/dashboard')

            return Promise.resolve(data)
        } catch (error) {
            ElMessage.error(error.msg || '登录失败')
            return Promise.reject(error)
        } finally {
            isLoading.value = false
        }
    }

    const logout = async () => {
        try {
            if (accessToken.value) {
                await fetch(`${API_URL}/work/auth/logout`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${accessToken.value}`
                    }
                })
            }
        } catch (error) {
            console.error('登出失败:', error)
        } finally {
            clearAuth()
            await router.push('/auth/login')
            ElMessage.success('已退出登录')
        }
    }

    const fetchProfile = async () => {
        if (!accessToken.value) {
            console.error('fetchProfile: 没有access token')
            throw new Error('未登录')
        }

        try {
            console.log('fetchProfile: 开始获取用户信息')
            const response = await fetch(`${API_URL}/work/auth/profile`, {
                headers: {
                    'Authorization': `Bearer ${accessToken.value}`
                }
            })

            console.log('fetchProfile: 响应状态:', response.status)

            if (response.status === 401) {
                console.log('fetchProfile: token无效或过期')
                clearAuth()
                throw new Error('登录已过期，请重新登录')
            }

            if (!response.ok) {
                console.error('fetchProfile: 响应失败:', response.status, response.statusText)
                throw new Error(`获取用户信息失败: ${response.status}`)
            }

            const data = await response.json()
            console.log('fetchProfile: 获取到数据:', data)

            let userData = null
            if (data.data) {
                userData = data.data // 如果响应有 data 字段
            } else if (data.user) {
                userData = data.user // 如果响应有 user 字段
            } else {
                userData = data // 直接就是用户数据
            }

            if (!userData) {
                console.error('fetchProfile: 无法解析用户数据')
                throw new Error('用户数据格式错误')
            }

            console.log('fetchProfile: 设置用户数据:', userData)
            setUser(userData)

            return userData
        } catch (error) {
            console.error('fetchProfile 失败:', error)
            // 尝试从localStorage恢复用户信息
            const savedUser = localStorage.getItem('user_info')
            if (savedUser) {
                try {
                    console.log('fetchProfile: 尝试从localStorage恢复')
                    const userData = JSON.parse(savedUser)
                    setUser(userData)
                    return userData
                } catch (e) {
                    console.error('fetchProfile: 恢复用户信息失败:', e)
                }
            }
            throw error
        }
    }

    const fixUserData = () => {
        if (!user.value) return

        console.log('修复用户数据前:', user.value)

        // 确保所有必要字段都有默认值
        user.value = {
            id: user.value.id || 0,
            username: user.value.username || '',
            email: user.value.email || '',
            full_name: user.value.full_name || '',
            phone: user.value.phone || '',
            department: user.value.department || '',
            role: user.value.role || 'user',
            is_admin: user.value.is_admin || false,
            is_active: user.value.is_active !== undefined ? user.value.is_active : true,
            avatar_url: user.value.avatar_url || user.value.avatar || '',
            last_login: user.value.last_login || null,
            created_at: user.value.created_at || null,
            updated_at: user.value.updated_at || null,
            permissions: user.value.permissions || [],
            user_groups: user.value.user_groups || [],
            data_scopes: user.value.data_scopes || []
        }

        console.log('修复用户数据后:', user.value)

        // 重新保存到 localStorage
        try {
            localStorage.setItem('user_info', JSON.stringify(user.value))
        } catch (e) {
            console.error('修复用户数据后保存失败:', e)
        }
    }

    // 初始化函数
    const init = async () => {
        console.log('初始化用户信息开始...')

        // 先检查是否有 token
        const hasToken = !!accessToken.value
        console.log('检查token:', {
            hasToken,
            accessToken: accessToken.value ? '存在' : '不存在',
            refreshToken: refreshToken.value ? '存在' : '不存在'
        })

        if (!hasToken) {
            console.log('没有token，跳过初始化')
            return null
        }

        try {
            // 首先尝试从 localStorage 恢复用户信息
            const savedUser = localStorage.getItem('user_info')
            console.log('从localStorage恢复用户信息:', savedUser ? '有数据' : '无数据')

            if (savedUser) {
                try {
                    const userData = JSON.parse(savedUser)
                    console.log('解析后的用户数据:', userData)
                    setUser(userData)
                    console.log('从localStorage恢复成功:', userData.username)
                } catch (parseError) {
                    console.error('解析localStorage用户信息失败:', parseError)
                    // 解析失败，清除错误数据
                    localStorage.removeItem('user_info')
                }
            }

            // 如果有token但用户信息为空或不完整，则从API获取
            const needsRefresh = !user.value ||
                !user.value.id ||
                !user.value.username ||
                !user.value.last_login

            console.log('检查是否需要刷新用户信息:', {
                hasUser: !!user.value,
                userId: user.value?.id,
                username: user.value?.username,
                lastLogin: user.value?.last_login,
                needsRefresh
            })

            if (needsRefresh) {
                console.log('用户信息不完整或为空，重新获取...')
                try {
                    await fetchProfile()
                    console.log('获取用户信息成功:', user.value?.username)
                } catch (fetchError) {
                    console.error('获取用户信息失败:', fetchError)

                    // 如果获取失败，检查token是否有效
                    if (fetchError.message.includes('401') ||
                        fetchError.message.includes('未授权')) {
                        console.log('token可能过期，清除认证信息')
                        clearAuth()
                        return null
                    }
                }
            }

            if (user.value) {
                fixUserData()
            }

            // 标记为已初始化（如果有用户信息）
            if (user.value) {
                globalState.setInitialized()
                console.log('初始化完成，用户信息:', {
                    id: user.value.id,
                    username: user.value.username,
                    email: user.value.email,
                    role: user.value.role,
                    department: user.value.department,
                    last_login: user.value.last_login
                })
            } else {
                console.log('初始化完成，但没有用户信息')
            }

            return user.value
        } catch (error) {
            console.error('初始化过程出错:', error)
            return null
        }
    }

    const resetSelfPassword = async (data) => {
        try {
            isLoading.value = true

            // 调用真实 API 重置密码
            const response = await fetch(`${API_URL}/work/auth/reset-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken.value}`
                },
                body: JSON.stringify(data)
            })

            if (!response.ok) {
                const errorData = await response.json()
                throw new Error(errorData.msg || '密码重置失败')
            }

            ElMessage.success('密码重置成功')
            return Promise.resolve()
        } catch (error) {
            ElMessage.error(error.msg || '密码重置失败')
            return Promise.reject(error)
        } finally {
            isLoading.value = false
        }
    }

    // 获取完整的用户信息（用于确保信息完整）
    const ensureUserInfoComplete = async () => {
        try {
            if (!accessToken.value) {
                throw new Error('未登录')
            }

            // 如果有基础用户信息但缺少详细字段，则获取完整信息
            if (user.value && (!user.value.department || !user.value.last_login)) {
                console.log('用户信息不完整，重新获取...')
                await fetchProfile()
            }

            return user.value
        } catch (error) {
            console.error('确保用户信息完整失败:', error)
            throw error
        }
    }

    return {
        // 状态
        user,
        accessToken,
        refreshToken,
        permissions,
        isLoading,

        // 计算属性
        isAuthenticated,
        isAdmin,
        isManager,
        userInitials,

        // 方法
        setTokens,
        setUser,
        mergeUserInfo,
        clearAuth,
        login,
        logout,
        fetchProfile,
        init,
        resetSelfPassword, // 新增方法
        ensureUserInfoComplete,
        fixUserData,
        updateProfile: async (data) => { // 新增：更新个人资料方法
            try {
                isLoading.value = true
                console.log('开始更新个人资料:', data)

                const response = await fetch(`${API_URL}/work/auth/profile`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken.value}`
                    },
                    body: JSON.stringify(data)
                })

                if (!response.ok) {
                    const errorData = await response.json()
                    throw new Error(errorData.message || '更新失败')
                }

                const result = await response.json()
                console.log('更新个人资料响应:', result)

                // 修复：正确处理响应数据
                let updatedUser = null

                if (result.data) {
                    // 如果响应有 data 字段
                    updatedUser = {
                        ...user.value, // 保留现有数据
                        ...result.data, // 用新数据覆盖
                        // 确保重要字段不被覆盖
                        avatar_url: user.value?.avatar_url || result.data.avatar_url,
                        is_admin: user.value?.is_admin || result.data.is_admin,
                        permissions: user.value?.permissions || result.data.permissions,
                        user_groups: user.value?.user_groups || result.data.user_groups,
                        last_login: user.value?.last_login || result.data.last_login,
                        created_at: user.value?.created_at || result.data.created_at,
                        updated_at: result.data.updated_at || new Date().toISOString()
                    }
                } else if (result.user) {
                    // 如果响应有 user 字段
                    updatedUser = {
                        ...user.value,
                        ...result.user,
                        avatar_url: user.value?.avatar_url || result.user.avatar_url,
                        is_admin: user.value?.is_admin || result.user.is_admin,
                        permissions: user.value?.permissions || result.user.permissions,
                        user_groups: user.value?.user_groups || result.user.user_groups,
                        last_login: user.value?.last_login || result.user.last_login,
                        created_at: user.value?.created_at || result.user.created_at,
                        updated_at: result.user.updated_at || new Date().toISOString()
                    }
                } else {
                    // 如果响应直接是用户数据
                    updatedUser = {
                        ...user.value,
                        ...result,
                        avatar_url: user.value?.avatar_url || result.avatar_url,
                        is_admin: user.value?.is_admin || result.is_admin,
                        permissions: user.value?.permissions || result.permissions,
                        user_groups: user.value?.user_groups || result.user_groups,
                        last_login: user.value?.last_login || result.last_login,
                        created_at: user.value?.created_at || result.created_at,
                        updated_at: result.updated_at || new Date().toISOString()
                    }
                }

                console.log('合并后的用户信息:', updatedUser)
                setUser(updatedUser)

                ElMessage.success('更新成功')
                return Promise.resolve(result)
            } catch (error) {
                console.error('更新个人资料失败:', error)
                ElMessage.error(error.message || '更新失败')
                return Promise.reject(error)
            } finally {
                isLoading.value = false
            }
        }

    }
})